import {
    Box,
    Button,
    Flex,
    FormControl,
    Heading,
    Input,
    Spinner,
    Text,
    Stat,
    StatLabel,
    StatNumber,
    SimpleGrid,
    useColorModeValue,
    SkeletonText,
} from '@chakra-ui/react';
import AppTableWithPagination from 'components/AppTableWithPagination';
import SidebarWithHeader from 'components/SidebarWithHeader';
import moment from 'moment';
import { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useGetActivityListQuery, useLazyGetExportDataQuery, useGetActiveClientCountQuery, useGetCampaignCountQuery, useGetActiveAdAccountCountQuery } from 'store/analytics/analyticsApi';
import { RepeatIcon } from '@chakra-ui/icons';
import { IconButton } from '@chakra-ui/react';


function ChakraDatePicker({ selectedDate, onChange, minDate, maxDate, isDisabled, placeholder }) {
    return (
        <FormControl display="flex" flexDirection="column" alignItems="flex-start" w="auto">
            <Box>
                <DatePicker
                    selected={selectedDate}
                    onChange={onChange}
                    customInput={<Input width="150px" fontSize="xs" size={'xs'} borderRadius={'md'} />}
                    dateFormat="yyyy-MM-dd"
                    minDate={minDate}
                    maxDate={maxDate}
                    placeholderText={placeholder}
                    disabled={isDisabled}
                    size="sm"
                />
            </Box>
        </FormControl>
    );
}

function StatsCard(props) {
    const { title, stat } = props
    return (
        <Stat
            px={{ base: 2, md: 8 }}
            py={'2'}
            border={'1px solid'}
            borderColor={useColorModeValue('gray.800', 'gray.500')}
            rounded={'lg'}>
            <StatLabel fontWeight={'medium'} isTruncated>
                {title}
            </StatLabel>
            <StatNumber fontSize={'xl'} fontWeight={'bold'}>
                {stat}
            </StatNumber>
        </Stat>
    )
}



function ActivityDashboard() {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const { data: activities, isLoading, isFetching } = useGetActivityListQuery({
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            searchTerm,
            page: currentPage
        },
    );

    const [triggerExportData, { data: exportData, isLoading: isExportDataLoading }] = useLazyGetExportDataQuery();

    const {
        data: activeClientCount,
        isLoading: isActiveClientCountLoading,
        isFetching: isActiveClientCountFetching,
    } = useGetActiveClientCountQuery({
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
    });

    const {
        data: campaignCount,
        isLoading: isCampaignCountLoading,
        isFetching: isCampaignCountFetching,
    } = useGetCampaignCountQuery({
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        searchTerm,
    });
    
    const {
        data: activeAdAccountCount,
        isLoading: isActiveAdAccountCountLoading,
        isFetching: isActiveAdAccountCountFetching,
    } = useGetActiveAdAccountCountQuery({
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        searchTerm,
    });
    

    const handleExport = async () => {
        const result = await triggerExportData({
          startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
          endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
          searchTerm,
        }).unwrap();
      
        // Create a Blob and trigger download
        const blob = new Blob([result], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'active_clients.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };

    const columns = useMemo(() => [
        {
            Header: 'Ad Account',
            accessor: 'adAccountName',
            Filter: "",
            filter: "",
            Cell: ({ row: { original } }) => (
                <Flex direction="column" mb={-4}>
                    <Text fontSize="14px">{original.adAccountName}</Text>
                    <Text fontSize="10px" color="gray.500">{original.adAccountId}</Text>
                </Flex>
            )
        },
        {
            Header: 'Total Campaigns',
            accessor: 'campaignCount',
            Filter: "",
            filter: "",
            Cell: ({ row: { original } }) => (
                <Flex direction="column" mb={-4}>
                    <Text fontSize="14px">{original?.campaignCount}</Text>
                </Flex>
            )
        },
        {
            Header: 'Live Campaign Count',
            accessor: 'liveCampaignCount',
            Filter: "",
            filter: "",
            Cell: ({ row: { original } }) => (
                <Flex direction="column" mb={-4}>
                    <Text fontSize="14px">{original?.liveCampaignCount}</Text>
                </Flex>
            )
        },
        {
            Header: 'Client',
            accessor: 'clientName',
            Filter: "",
            filter: "",
            Cell: ({ row: { original } }) => (
                <Text fontSize="14px">{original.clientName}</Text>
            )
        },
        // {
        //     Header: "Created Date",
        //     accessor: "createdDate",
        //     Filter: "",
        //     filter: "",
        //     Cell: ({ row: { original } }) => (
        //         <Text fontSize="14px">{moment(original.createdDate).format('DD-MM-YYYY')}</Text>
        //     )
        // },
    ], []);

    const emptyMessage = (
        <Flex flexDirection="column" alignItems="center" justifyContent="center" mt={4} gap={'10px'}>
            <Text mt={4} textAlign="center" color="gray.500">
                No activities found, please try adjusting the filters.
            </Text>
            <Button
                size={'xs'}
                onClick={() => {
                    setSearchTerm('');
                    setStartDate('');
                    setEndDate('');
                }}
            >Reset</Button>
        </Flex>
    );

    return (
        <SidebarWithHeader>
            <Flex justify="space-between" mb={4} gap={'10px'}>
                <Flex direction="column" gap={'10px'}>
                    <Heading size="lg">Active Clients</Heading>
                    <Text fontSize={'14px'}>View the number of campaigns successfully launched per ad account, along with associated client details</Text>
                </Flex>
                <Flex direction={'column'}>
                    <Flex flex={0.5} justifyContent={'flex-end'} pr={10}>
                    <Button onClick={handleExport} isLoading={isExportDataLoading} size={'xs'}>
                        Export
                    </Button>

                    </Flex>
                    <Flex gap={'10px'} flex={0.5}>
                        <ChakraDatePicker
                            label="Start Date"
                            selectedDate={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            placeholder="Start Date"
                            isDisabled={!activities || !activities?.data?.length}
                        />
                        <ChakraDatePicker
                            label="End Date"
                            selectedDate={endDate}
                            onChange={setEndDate}
                            minDate={startDate}
                            maxDate={new Date()}
                            placeholder="End Date"
                            isDisabled={!activities || !activities?.data?.length}
                        />
                        <IconButton
                            aria-label="Reset date range"
                            icon={<RepeatIcon />}
                            size="xs"
                            variant="outline"
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                            isDisabled={isFetching}
                        />
                    </Flex>
                </Flex>
            </Flex>

            <Flex mb={4}>
                <>
                    {(isActiveClientCountLoading || isActiveAdAccountCountLoading || isCampaignCountLoading ) ? (
                        <Spinner my={2} />
                    ) : (
                        <Flex direction="column" gap={6} width={'100%'}>
                            {/* row of Stats */}
                            <SimpleGrid templateColumns="repeat(4, 1fr)" spacing={{ base: 5, lg: 8 }} w="100%">
                                <StatsCard 
                                    title="Active Clients" 
                                    stat={activeClientCount ? activeClientCount.clientCount : '-'} 
                                />
                                <StatsCard 
                                    title="Active Ad Accounts" 
                                    stat={activeAdAccountCount ? activeAdAccountCount.activeAdAccountCount : '-'} 
                                />
                                <StatsCard 
                                    title="Total Campaigns" 
                                    stat={campaignCount ? campaignCount.totalCampaigns : '-'} 
                                />
                                <StatsCard 
                                    title="Active Campaigns" 
                                    stat={campaignCount ? campaignCount.activeCampaigns : '-'} 
                                />
                            </SimpleGrid>
                        </Flex>
                    )}
                </>
            </Flex>

            {isLoading ? (
                <Flex minH="400px" align="center" justify="center">
                    <Spinner mt="20px" />
                </Flex>
            ) : (
                <Box>
                    <AppTableWithPagination
                        columns={columns}
                        data={activities?.data}
                        paginationMeta={activities?.meta}
                        onPageChange={(index) => setCurrentPage(index)}
                        isFetching={isFetching}
                        onSearchInputChange={(searchInput) => setSearchTerm(searchInput)}
                        searchEnabled={true}
                        emptyMessage={emptyMessage}
                    />
                </Box>
            )}
        </SidebarWithHeader>
    );
}

export default ActivityDashboard;
